import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import { API_ENDPOINTS } from "../config/api";
import { clearCompanyLogoCache } from "../utils/companyLogo";

type User = {
  user_id: number;
  full_name: string;
  email: string;
  roleid?: number | string | null;
  role?: string;
  company?: {
    company_id: number;
    name: string;
    trading_name: string;
    status: string;
    biometrics?: boolean;
  };
};

type AuthContextValue = {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (user: User) => void;
  logout: () => void;
  refreshUser: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = "user";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  type MeResponseOk = {
    user: { user_id: number; full_name: string; email: string };
    company?: User["company"] | null;
    role?: { role_id: number; label: string | null };
  };

  // Load user from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as User;
        setUser(parsed);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const validateSession = async () => {
      try {
        const res = await api.get<MeResponseOk>(API_ENDPOINTS.me);
        const u = res.data?.user;
        if (!u || typeof u.user_id !== 'number') {
          throw new Error('Invalid /me response');
        }
        const roleData = res.data?.role;

        // Preserve fields like `company` that /me doesn't return.
        // The effect closure captures a stale `user` value so read localStorage directly.
        let stored: User | null = null;
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) stored = JSON.parse(raw) as User;
        } catch { /* ignore */ }

        // Refresh company details (e.g. the biometrics flag) from /me when it returns them.
        const companyData = res.data?.company ?? undefined;
        const nextUser: User = {
          ...(stored ?? {}),
          user_id: u.user_id,
          full_name: u.full_name,
          email: u.email,
          roleid: roleData?.role_id ?? null,
          role: roleData?.label ?? '',
          ...(companyData ? { company: { ...(stored?.company ?? {}), ...companyData } } : {}),
        };

        if (!cancelled) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
          setUser(nextUser);
          // Warm the server-side biometrics cache so payslip/report requests
          // skip individual biometrics network calls. Fire-and-forget.
          api.post('/payroll/payslips/preload-attendance').catch(() => {});
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsInitialized(true);
        }
      }
    };

    validateSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback((userData: User) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    clearCompanyLogoCache();
    setUser(null);
  }, []);

  const refreshUser = useCallback(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const res = await api.get<MeResponseOk>(API_ENDPOINTS.me);
        const u = res.data?.user;
        if (!u || typeof u.user_id !== 'number') {
          throw new Error('Invalid /me response');
        }
        const roleData = res.data?.role;

        const nextUser: User = {
          user_id: u.user_id,
          full_name: u.full_name,
          email: u.email,
          roleid: roleData?.role_id ?? null,
          role: roleData?.label ?? '',
          ...(res.data?.company ? { company: res.data.company } : {}),
        };

        if (!cancelled) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
          setUser(nextUser);
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setUser(null);
          return;
        }

        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as User;
            setUser(parsed);
          } else {
            setUser(null);
          }
        } catch {
          setUser(null);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isInitialized,
      login,
      logout,
      refreshUser,
    }),
    [user, isInitialized, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
